import { z } from 'zod';
// Meta history ingest schemas (phase 1 of the meta-cm roadmap).
//
// When the founder connects an Instagram business account, Mag walks
// the full back-catalogue of posts to learn the brand from the
// inside — voice patterns (caption style, length, hooks, emoji use,
// hashtag strategy) + visual patterns (format mix, color/composition
// signals, recurring themes). The output flows into the brand brain
// as the starting reference; later phases feed engagement deltas
// back to evolve it.
//
// What lives here:
//   - igPostShape: the shape we keep per-post after fetching from the
//     Graph API. Trimmed from what Graph returns — we drop fields we
//     don't need so jsonb payload stays small.
//   - voiceLearnOutput: what the meta-voice-learn agent produces from
//     a batch of captions. Translates to brand_brain.voice diffs.
//   - visualLearnOutput: what the meta-visual-learn agent produces
//     from a batch of media. Lands as brand_brain.visual_patterns.
//
// These are platform-agnostic where we can swing it (the "post" shape
// fits FB Page posts later with minimal stretching); IG-specific fields
// stay on a `provider_raw` escape hatch.
/** One Instagram media item after a Graph API fetch + our trim. */
export const igPostSchema = z.object({
    /** Graph's media id — stable identifier we'll use to dedupe on
     *  subsequent rescans. */
    id: z.string().min(1).max(120),
    /** IG's `media_type` value. Carousel is `CAROUSEL_ALBUM`, reels are
     *  `VIDEO` with `media_product_type=REELS`. */
    mediaType: z.enum(['IMAGE', 'VIDEO', 'CAROUSEL_ALBUM']),
    /** `media_product_type` from Graph — distinguishes reels from regular
     *  videos and feed-vs-story when present. Free-form because Meta
     *  ships new values without a code change. */
    mediaProductType: z.string().min(1).max(40).nullable(),
    /** Caption text the founder posted with the media. May be empty
     *  string for media-only posts. */
    caption: z.string().max(4000),
    /** Permalink URL to the post on Instagram. Stable across edits. */
    permalink: z.string().url(),
    /** Direct URL to the image / first carousel slide / video thumbnail.
     *  Meta rotates these every ~6h so we re-fetch on each scan. */
    mediaUrl: z.string().url().nullable(),
    /** When the post went live. ISO string. */
    timestamp: z.string(),
    /** Engagement counters at fetch time. These drift over time —
     *  capturing the snapshot is the point. */
    likeCount: z.number().int().min(0),
    commentsCount: z.number().int().min(0),
    /** Hashtag tokens parsed from the caption ('#brand' → 'brand'),
     *  lowercased + deduped. Empty array if none. */
    hashtags: z.array(z.string().min(1).max(80)),
    /** Carousel child media URLs in display order, if media_type is
     *  CAROUSEL_ALBUM. Empty array for single-media posts. */
    carouselChildren: z.array(z.string().url()).max(20).default([]),
});
// ─── Voice learn ──────────────────────────────────────────────────────
/** Recurring caption pattern Mag extracts — a hook style, a sign-off,
 *  an opener, etc. Each one has a description + 2-3 verbatim examples
 *  pulled from the founder's actual posts so the founder reads it as
 *  "yes that's me." */
export const voicePatternSchema = z.object({
    /** Short label — "question hooks", "single-emoji openers",
     *  "lowercase always", "two-sentence captions". Lowercase. */
    label: z.string().min(1).max(60),
    /** One-sentence description of the pattern. Lowercase, mag voice. */
    description: z.string().min(1).max(280),
    /** 2-3 verbatim caption snippets where this pattern shows up,
     *  trimmed to ≤120 chars each. The founder reads these to confirm. */
    examples: z.array(z.string().min(1).max(160)).min(1).max(5),
});
/** What meta-voice-learn produces from a batch of captions. The
 *  runner turns this into brand_brain.voice diffs (mustUseWords,
 *  voiceExamples, etc.) and lands a meta_voice_learn feed card. */
export const metaVoiceLearnOutputSchema = z.object({
    /** Number of captions the agent read to produce this output. */
    captionsAnalyzed: z.number().int().min(0),
    /** Mag's one-line summary of the voice she heard. Goes into the
     *  feed-card title. */
    summary: z.string().min(1).max(280),
    /** Tone-axes calibration. Mag rates each axis 0-10 based on the
     *  reading. Maps directly into brand_brain.voice.tone_axes. */
    toneAxes: z.object({
        formal_casual: z.number().int().min(0).max(10),
        serious_playful: z.number().int().min(0).max(10),
        expert_friendly: z.number().int().min(0).max(10),
    }),
    /** Words/phrases the founder uses repeatedly — fold into
     *  brand_brain.voice.must_use_words. */
    recurringWords: z.array(z.string().min(1).max(60)).max(20),
    /** Specific patterns mag picked up (hook styles, sign-offs, etc.). */
    patterns: z.array(voicePatternSchema).min(1).max(8),
    /** Verbatim caption snippets that best represent the voice — fold
     *  into brand_brain.voice.voice_examples. Cap at 1000 chars per
     *  example: tweet-length (280) is too tight (real IG captions
     *  often hit 600-800) and the IG ceiling (2200) ruins prompt
     *  budget when voice_examples gets re-injected into slot-draft. */
    exemplarCaptions: z.array(z.string().min(1).max(1000)).min(1).max(6),
    /** Mag's confidence on the read, 0-10. Lower when the back-catalogue
     *  is small (<10 posts) or the captions are inconsistent. Surfaces
     *  in the feed card as "i'm <confidence> on this — review carefully"
     *  vs "this is consistent enough to apply directly." */
    confidence: z.number().int().min(0).max(10),
});
// ─── Visual learn ─────────────────────────────────────────────────────
/** Recurring visual pattern — a recurring color, composition rule,
 *  format preference, etc. */
export const visualPatternSchema = z.object({
    /** Short label — "white space + bold sans serif", "no faces",
     *  "earth-tone palette", "carousel-first". */
    label: z.string().min(1).max(60),
    /** One-sentence description. */
    description: z.string().min(1).max(280),
    /** Estimated share of posts that follow this pattern, 0-100. Mag
     *  reads this conservatively — 60+ means "consistent." */
    prevalencePercent: z.number().int().min(0).max(100),
});
/** Format-mix breakdown — how often the founder posts each format.
 *  Numbers are percentages of the analyzed batch summing to ~100. */
export const formatMixSchema = z.object({
    staticImage: z.number().int().min(0).max(100),
    carousel: z.number().int().min(0).max(100),
    reel: z.number().int().min(0).max(100),
    video: z.number().int().min(0).max(100),
});
/** [12.2L-167] Structured brand-slot mapping the visual-learn agent
 *  emits alongside the prose patterns. Mirrors visual-vision.ts's
 *  brandSlots shape so applyVisualToBrain can merge straight into
 *  brand_brain.design_system. Every field optional — only filled
 *  when the agent has strong pixel-level signal.
 *
 *  Colors are hex (the agent extracts them by eye); typography are
 *  CSS font-family strings (the agent picks from what it can identify
 *  in the screenshots). */
export const metaVisualLearnBrandSlotsSchema = z.object({
    colors: z
        .object({
        /** CTA fill + brand-mark accent. Often the bright/saturated one. */
        primary: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
        /** Body text + dark canvas. Usually near-black / deep ink. */
        ink: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
        /** Dominant light canvas. Off-white, cream, beige. */
        paper: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
        /** Secondary canvas — alternate background when the brand
         *  alternates (e.g. gestionalo's navy ↔ warm off-white). */
        secondary: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
        /** Tertiary accent — category-label color, soft highlights. */
        accent: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
    })
        .optional(),
    typography: z
        .object({
        /** Dominant headline face — bold sans for most brands. */
        display: z.string().min(1).max(120).optional(),
        /** Paragraph body face. Often shares family with display. */
        body: z.string().min(1).max(120).optional(),
        /** Italic / serif / script accent face — second-clause emphasis.
         *  Omit when the brand uses a single typeface end-to-end. */
        accent: z.string().min(1).max(120).optional(),
        /** Monospace face. Omit unless distinctive. */
        mono: z.string().min(1).max(120).optional(),
        /** [12.2L-168] Per-slot weight. Without these, weights stick at
         *  whatever the onboarding-time visual scraper inferred — which
         *  is frequently wrong (gestionalo's italic was scraped as 700
         *  but the brand uses 400). Each is a CSS font-weight integer:
         *  100=thin, 400=regular, 700=bold, 900=black. The agent picks
         *  the weight the brand actually uses based on the rendered IG
         *  posts, not what the marketing site happens to declare. */
        displayWeight: z.number().int().min(100).max(900).optional(),
        bodyWeight: z.number().int().min(100).max(900).optional(),
        accentWeight: z.number().int().min(100).max(900).optional(),
    })
        .optional(),
    /** Structural patterns to surface as design_system motifs — pill
     *  cards, split-screen layouts, etc. One short label per. */
    motifs: z.array(z.string().min(1).max(80)).max(8).optional(),
});
/** What meta-visual-learn produces from a batch of media. */
export const metaVisualLearnOutputSchema = z.object({
    postsAnalyzed: z.number().int().min(0),
    /** One-line summary for the feed card. */
    summary: z.string().min(1).max(280),
    formatMix: formatMixSchema,
    patterns: z.array(visualPatternSchema).min(1).max(8),
    /** Colors that recur — mag describes them in plain language ("warm
     *  off-white background", "deep ink-blue accent") since LLM reading
     *  of images doesn't give us pixel-perfect hex. Founder + visual-
     *  scraper agent can refine. Cap is 200 (was 80, which crashed
     *  parsing when the agent emitted role + tone prose for any color
     *  that doesn't fit a 5-word tag). */
    recurringColors: z.array(z.string().min(1).max(200)).max(8),
    /** [12.2L-167] Structured slot mapping — what applyVisualToBrain
     *  reads to update brand_brain.design_system. Optional because old
     *  card rows pre-date this addition; new runs emit it whenever the
     *  agent has strong enough signal. */
    brandSlots: metaVisualLearnBrandSlotsSchema.optional(),
    confidence: z.number().int().min(0).max(10),
});
// ─── Combined run output ──────────────────────────────────────────────
/** The runner that orchestrates a full learn returns this shape — both
 *  agent outputs + bookkeeping. The api wraps it into a feed card +
 *  optionally proposes brand_brain diffs through the existing brand-
 *  coach flow. */
export const metaHistoryLearnResultSchema = z.object({
    voice: metaVoiceLearnOutputSchema,
    visual: metaVisualLearnOutputSchema,
    /** Total cost in USD across both agent calls. */
    costUsd: z.number().min(0),
    /** Wall-clock from fetch-start to both-agents-returned. */
    durationMs: z.number().min(0),
});
// ─── Structural signals (non-LLM stats) ───────────────────────────────
/** Cheap stats we compute directly from the IgPost batch without an
 *  LLM call. These are deterministic + zero-cost — they surface in
 *  the feed card alongside Mag's voice read as the "by the numbers"
 *  block. */
export const metaStructuralSignalsSchema = z.object({
    /** Total posts analyzed. */
    postsAnalyzed: z.number().int().min(0),
    /** Format mix as percentages (sum to ~100). Same shape as the
     *  visual-learn output's formatMix so the dashboard renders them
     *  with one component. */
    formatMix: formatMixSchema,
    /** Posting frequency, posts per week (median over the analyzed
     *  window). Null when there's <2 weeks of history to median over. */
    postsPerWeek: z.number().min(0).nullable(),
    /** Day-of-week distribution. Numbers are counts (NOT percentages).
     *  Mon=0 ... Sun=6 to match JS Date.getUTCDay() shifted. */
    postsByDayOfWeek: z.array(z.number().int().min(0)).length(7),
    /** Top 3 posts by engagement (likes + comments). Carries the post
     *  id + the engagement count so the founder can verify on IG. */
    topEngagementPosts: z
        .array(z.object({
        postId: z.string(),
        permalink: z.string().url(),
        caption: z.string().max(280),
        likeCount: z.number().int().min(0),
        commentsCount: z.number().int().min(0),
        engagement: z.number().int().min(0),
    }))
        .max(5),
});
// ─── Feed card payload ────────────────────────────────────────────────
/** Feed card that lands after Mag has read the brand's IG history.
 *  Carries the voice read + structural signals + (later phase 1b) the
 *  visual read. The founder can ack to acknowledge or dismiss; an
 *  "apply to brand brain" CTA lands the recurring words + exemplar
 *  captions + tone axes as a brand_evolution proposal. */
export const metaHistoryLearnCardPayloadSchema = z.object({
    kind: z.literal('meta_history_learn'),
    /** Mag's voice read. Null when the connection has no captioned
     *  posts yet — the card still surfaces "i connected but there's
     *  nothing to learn from yet." */
    voice: metaVoiceLearnOutputSchema.nullable(),
    /** Cheap stats — always present, even on empty history (with zeros). */
    signals: metaStructuralSignalsSchema,
    /** Will hold the visual-learn output once vision lands in the LLM
     *  router. Nullable today so the card lands without it. */
    visual: metaVisualLearnOutputSchema.nullable(),
    /** Cost of the LLM call(s) that produced this read, in USD. Surfaces
     *  in the card footer for transparency. */
    costUsd: z.number().min(0),
    /** What changed in the brand brain when mag applied the read.
     *  Null when nothing was applied (empty history / low confidence
     *  / dedupe left nothing new to add). */
    applied: z
        .object({
        brainVersionBefore: z.number().int().min(1),
        brainVersionAfter: z.number().int().min(1),
        mustUseWordsAdded: z.array(z.string()).max(30),
        voiceExamplesAdded: z.array(z.string()).max(12),
        /** [12.2L-167] Design-system diff applied from visual-learn's
         *  brandSlots. Null when visual-learn didn't run, didn't clear
         *  the confidence bar, or produced no slot changes. Each field
         *  is the BEFORE → AFTER pair so the founder reads what mag
         *  changed without diffing JSON blobs. */
        designSystemDiff: z
            .object({
            paletteChanges: z
                .array(z.object({
                slot: z.enum(['primary', 'ink', 'paper', 'secondary', 'accentAlt', 'support']),
                before: z.string().nullable(),
                after: z.string(),
            }))
                .max(8),
            typographyChanges: z
                .array(z.object({
                slot: z.enum(['display', 'body', 'mono', 'accent']),
                before: z.string(),
                after: z.string(),
            }))
                .max(4),
            /** [12.2L-168] Per-slot weight changes applied to
             *  design_system.typography.weights. Empty array when the
             *  agent emitted no weight diff. */
            typographyWeightChanges: z
                .array(z.object({
                slot: z.enum(['display', 'body', 'accent']),
                before: z.number().int(),
                after: z.number().int(),
            }))
                .max(3)
                .optional(),
            motifsAdded: z.array(z.string()).max(8),
        })
            .nullable()
            .optional(),
    })
        .nullable(),
});
//# sourceMappingURL=meta-history.js.map